import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "../config";

type LiveState = "live" | "reconnecting" | "offline" | "polling";

const LiveContext = createContext<{ state: LiveState; lastUpdate?: string }>({ state: "offline" });

export function LiveProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveState>("reconnecting");
  const [lastUpdate, setLastUpdate] = useState<string>();
  const attempt = useRef(0);
  const polling = useRef<number>();
  const refreshTimers = useRef<{ public?: number; status?: number }>({});
  const lastRefreshAt = useRef({ public: 0, status: 0 });

  useEffect(() => {
    let ws: WebSocket | undefined;
    let closed = false;
    let reconnectTimer: number | undefined;

    const invalidatePublicQueries = () => {
      setLastUpdate(new Date().toISOString());
      void queryClient.invalidateQueries({ queryKey: ["home"] });
      void queryClient.invalidateQueries({ queryKey: ["global-stats"] });
      void queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      void queryClient.invalidateQueries({ queryKey: ["matches"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
      void queryClient.invalidateQueries({ queryKey: ["players"] });
      void queryClient.invalidateQueries({ queryKey: ["player"] });
      void queryClient.invalidateQueries({ queryKey: ["team"] });
      void queryClient.invalidateQueries({ queryKey: ["event"] });
      void queryClient.invalidateQueries({ queryKey: ["match"] });
      void queryClient.invalidateQueries({ queryKey: ["event-qualifier"] });
      void queryClient.invalidateQueries({ queryKey: ["event-qualifier-game"] });
      void queryClient.invalidateQueries({ queryKey: ["event-qualifier-match"] });
    };

    const invalidateStatusQueries = () => {
      setLastUpdate(new Date().toISOString());
      void queryClient.invalidateQueries({ queryKey: ["status"] });
    };

    const scheduleRefresh = (bucket: "public" | "status", intervalMs: number, run: () => void) => {
      const elapsed = Date.now() - lastRefreshAt.current[bucket];
      const delay = Math.max(0, intervalMs - elapsed);
      if (refreshTimers.current[bucket]) return;
      refreshTimers.current[bucket] = window.setTimeout(() => {
        delete refreshTimers.current[bucket];
        lastRefreshAt.current[bucket] = Date.now();
        run();
      }, delay);
    };

    const handleLiveMessage = (data: string) => {
      if (data === "pong") return;
      try {
        const payload = JSON.parse(data) as { type?: string };
        if (payload.type === "connected") {
          setLastUpdate(new Date().toISOString());
          return;
        }
        if (payload.type === "heartbeat") {
          scheduleRefresh("status", 10_000, invalidateStatusQueries);
          return;
        }
      } catch {
        // Non-JSON messages still mean something changed, but keep them batched.
      }
      scheduleRefresh("public", 5_000, invalidatePublicQueries);
    };

    const startPolling = () => {
      if (polling.current) return;
      setState("polling");
      polling.current = window.setInterval(() => scheduleRefresh("public", 15_000, invalidatePublicQueries), 15_000);
    };

    const stopPolling = () => {
      if (polling.current) window.clearInterval(polling.current);
      polling.current = undefined;
    };

    const connect = () => {
      const base = API_URL || window.location.origin;
      const wsUrl = base.replace(/^http/, "ws") + "/api/v1/realtime";
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        startPolling();
        return;
      }
      ws.onopen = () => {
        attempt.current = 0;
        stopPolling();
        setState("live");
        ws?.send("ping");
      };
      ws.onmessage = (event) => {
        if (typeof event.data === "string") handleLiveMessage(event.data);
        else scheduleRefresh("public", 5_000, invalidatePublicQueries);
      };
      ws.onclose = () => {
        if (closed) return;
        setState("reconnecting");
        startPolling();
        const delay = Math.min(30_000, 1000 * 2 ** attempt.current++);
        reconnectTimer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => {
        setState("offline");
        startPolling();
      };
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      stopPolling();
      if (refreshTimers.current.public) window.clearTimeout(refreshTimers.current.public);
      if (refreshTimers.current.status) window.clearTimeout(refreshTimers.current.status);
      ws?.close();
    };
  }, [queryClient]);

  const value = useMemo(() => lastUpdate ? { state, lastUpdate } : { state }, [state, lastUpdate]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive() {
  return useContext(LiveContext);
}
